// ===== World 15: Ocean disco – WATER AND AIR FOR ADVANCED PLAYERS =====
// The whole level is under water: things fall slowly (water drag) and light things rise (buoyancy).
// Currents push, bubbles and air-filled things lift. The bubble balloon carries the pearl up and pops,
// and the submarine float rises when filled with air and sinks when the air is let out.
// The hero is a pearl with a face, and the goal is the open clam shell (the pearl comes home).
import { defineWorld, definePart, defineObj } from '../../core/registry';
import { rr, outline, face, clamp, DEG } from '../../core/draw-helpers';
import type { Ctx2D, Gfx, GameBody, GoalState, Level, ObjData, ObjState, Placement, PhysicsApi, SkinDef, SoundFn, WorldDef, WorldMeta } from '../../core/types';
import { scene } from './scene';
import { levels } from './levels';
import { strings } from './strings';
import { K } from './shared';

// ---------- constants ----------
const INK = '#3B2A5C';
const G = 0.2778;                 // velocity gained from gravity per frame (gravity 1)

/** Current zone (conch or level current): start point, direction, length, width and speed */
interface Zone { x: number; y: number; dx: number; dy: number; len: number; wid: number; v: number }
/** Bubble column: start point, direction, length, width and lift per frame */
interface Column { x: number; y: number; dx: number; dy: number; len: number; wid: number; pow: number }
/** A rising bubble in a column (distance s along it, sideways p, radius r, wobble phase ph) */
interface Bub { s: number; p: number; r: number; ph: number }
interface Urchin { x: number; y: number; r: number }

declare module '../../core/types' {
  interface SimState { oceanZones?: Zone[]; oceanUrchins?: Urchin[] }
  interface GameBody {
    /** Float number (see floatOf); null = default for its kind */
    oceanF?: number | null;
    /** Carried by a bubble balloon (water and currents leave it alone) */
    oceanHeld?: boolean;
    /** Frames of weak drag left after a jellyfish hop */
    oceanBoost?: number;
  }
  interface Ball { oceanF?: number }
}

// Level data of the world's objects (o in create/step/draw)
interface CurrentData extends ObjData { a?: number; len?: number; wid?: number; v?: number }
interface FloatData extends ObjData { w?: number; air?: number; id?: string; minY?: number; maxY?: number }
interface PumpData extends ObjData { a?: number; id?: string; len?: number }
interface UrchinData extends ObjData { r?: number }
/** An object's state as physics passes it in (st.o is the level data) */
type ObjSt<T, O extends ObjData> = T & ObjState & { o: O };

// ---------- water physics ----------
// float number: below 1 sinks (the smaller, the faster), above 1 rises
function floatOf(b: GameBody) {
  if (b.oceanF != null) return b.oceanF;
  if (b.kind === 'hero' || b.kind === 'helper') return 0.6;
  if (b.kind === 'stone') return 0.25;
  return 0.5;
}
function water(api: PhysicsApi, level: Level) {
  // water drag grows with speed: fast things are slowed a lot, slow things only a little
  const drag = level.drag as number | undefined;
  const { Body } = api, S = api.S, c = drag != null ? drag : 0.022;
  for (const b of S.dyn) {
    if (b.isStatic || b.kind === 'seesaw') continue;
    if (b.oceanHeld) continue;
    const up = G * api.engine.gravity.y * floatOf(b);
    // right after a jellyfish hop the water barely slows it down
    const k = b.oceanBoost && b.oceanBoost > 0 ? (b.oceanBoost--, 0.99) : 1 - Math.min(0.08, 0.005 + c * b.speed);
    Body.setVelocity(b, { x: b.velocity.x * k, y: (b.velocity.y - up) * k });
    Body.setAngularVelocity(b, b.angularVelocity * 0.95);
  }
}
// current zone: velocity is pulled softly towards the current's velocity
function current(api: PhysicsApi, Z: Zone) {
  const { Body } = api;
  for (const b of api.S.dyn) {
    if (b.isStatic || b.kind === 'seesaw' || b.oceanHeld) continue;
    const dx = b.position.x - Z.x, dy = b.position.y - Z.y;
    const al = dx * Z.dx + dy * Z.dy, pe = dx * -Z.dy + dy * Z.dx;
    if (al < 0 || al > Z.len || Math.abs(pe) > Z.wid / 2) continue;
    const fade = Math.min(1, (Z.len - al) / 60);
    const k = 0.08 * fade;
    Body.setVelocity(b, { x: b.velocity.x + (Z.dx * Z.v - b.velocity.x) * k, y: b.velocity.y + (Z.dy * Z.v - b.velocity.y) * k });
  }
}
function zones(api: PhysicsApi) { return api.S.oceanZones || (api.S.oceanZones = []); }
function inZone(Z: Zone, x: number, y: number) { const dx = x - Z.x, dy = y - Z.y, al = dx * Z.dx + dy * Z.dy, pe = dx * -Z.dy + dy * Z.dx; return al >= 0 && al <= Z.len && Math.abs(pe) <= Z.wid / 2; }
// bubble column: lifts everything in the column and keeps it in the middle
function column(api: PhysicsApi, C: Column) {
  const { Body } = api;
  for (const b of api.S.dyn) {
    if (b.isStatic || b.kind === 'seesaw' || b.oceanHeld) continue;
    const dx = b.position.x - C.x, dy = b.position.y - C.y;
    const al = dx * C.dx + dy * C.dy, pe = dx * -C.dy + dy * C.dx;
    if (al < -10 || al > C.len || Math.abs(pe) > C.wid / 2 + 6) continue;
    const fade = al > C.len - 60 ? (C.len - al) / 60 : 1;
    const a = C.pow * fade;
    const vp = b.velocity.x * -C.dy + b.velocity.y * C.dx, dv = -vp * 0.08 - pe * 0.004;
    Body.setVelocity(b, { x: b.velocity.x + C.dx * a + -C.dy * dv, y: b.velocity.y + C.dy * a + C.dx * dv });
  }
}

// ---------- drawing helpers ----------
// differs from draw-helpers bubble: fill alpha .3 (not .35) and no save/restore
function bubble(ctx: Ctx2D, x: number, y: number, r: number) {
  ctx.beginPath(); ctx.arc(x, y, r, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.3)'; ctx.fill();
  ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(255,255,255,.95)'; ctx.stroke();
  ctx.beginPath(); ctx.arc(x - r * .35, y - r * .35, r * .22, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
}
// The pearl (hero) – white and shiny with a rainbow sheen.
// differs from draw-helpers drawBall: outline first, rainbow sheen, face rolls at 0.3x, gloss .9
function drawBall(ctx: Ctx2D, b: GameBody, kind: string, t: number) {
  const { x, y } = b.position, r = b.circleRadius!;
  ctx.save(); ctx.translate(x, y);
  const gr = ctx.createRadialGradient(-r * .35, -r * .4, r * .1, 0, 0, r * 1.05);
  if (kind === 'helper') { gr.addColorStop(0, '#FFF6D8'); gr.addColorStop(0.5, '#FFD27A'); gr.addColorStop(1, '#E09A2A'); }
  else { gr.addColorStop(0, '#FFFFFF'); gr.addColorStop(0.55, '#F3EAF7'); gr.addColorStop(1, '#C9B6E0'); }
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.fillStyle = gr; ctx.fill(); outline(ctx, 2.5);
  // rainbow sheen
  ctx.save(); ctx.beginPath(); ctx.arc(0, 0, r - 2, 0, 7); ctx.clip();
  const sh = (t * 0.02) % (Math.PI * 2);
  ctx.globalAlpha = 0.35;
  ['#FF8FB8', '#FFE07A', '#8FE6C0', '#8FC8FF'].forEach((c, i) => { ctx.beginPath(); ctx.arc(Math.cos(sh + i) * r * .4, Math.sin(sh + i) * r * .4, r * .45, 0, 7); ctx.fillStyle = c; ctx.fill(); });
  ctx.restore();
  // eyes follow at 0.6, no scared face, pinker cheeks than the default
  ctx.save(); ctx.rotate((b.roll || 0) * 0.3); face(ctx, r, b.velocity, b.sad, { look: 0.6, scared: false, ink: INK, cheek: 'rgba(255,120,160,.5)' }); ctx.restore();
  ctx.beginPath(); ctx.ellipse(-r * .38, -r * .45, r * .26, r * .15, -0.6, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.9)'; ctx.fill();
  ctx.restore();
}
// The clam shell (bowl): lower shell with ridges and the open lid behind it
function shellBack(ctx: Ctx2D, x: number, y: number, w: number, open: number, g: Gfx) {
  // the lid (behind, open)
  ctx.save(); ctx.translate(x, y - 58); ctx.rotate(-0.25 - open * 0.35);
  ctx.beginPath(); ctx.moveTo(-w / 2 - 4, 0); ctx.quadraticCurveTo(-w / 2, -w * 0.55, 0, -w * 0.6); ctx.quadraticCurveTo(w / 2, -w * 0.55, w / 2 + 4, 0); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -w * 0.6, 0, '#FFE3EE', '#F5A7C4'); ctx.fill(); outline(ctx, 3);
  ctx.strokeStyle = 'rgba(200,90,140,.45)'; ctx.lineWidth = 2.5;
  for (let i = -3; i <= 3; i++) { ctx.beginPath(); ctx.moveTo(i * w * 0.02, -4); ctx.lineTo(i * w * 0.14, -w * 0.52); ctx.stroke(); }
  ctx.restore();
}
function shellBowl(ctx: Ctx2D, gl: { x: number; y: number; w: number }, full: number, t: number, g: Gfx) {
  const { x, y, w } = gl;
  ctx.save();
  shellBack(ctx, x, y, w, full ? 1 : 0, g);
  g.shadowOn(ctx, 10, 5);
  ctx.beginPath(); ctx.moveTo(x - w / 2 - 6, y - 58); ctx.quadraticCurveTo(x - w / 2 + 2, y + 10, x, y + 10); ctx.quadraticCurveTo(x + w / 2 - 2, y + 10, x + w / 2 + 6, y - 58); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, y - 60, y + 10, '#FFE3EE', '#F08DB4'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3);
  ctx.save(); ctx.clip();
  ctx.strokeStyle = 'rgba(200,90,140,.45)'; ctx.lineWidth = 3;
  for (let i = -3; i <= 3; i++) { ctx.beginPath(); ctx.moveTo(x + i * w * 0.15, y - 58); ctx.lineTo(x + i * w * 0.04, y + 8); ctx.stroke(); }
  ctx.restore();
  ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 + 6, 8, 0, 0, 7); ctx.fillStyle = '#C9608E'; ctx.fill(); outline(ctx, 2.5);
  if (full) { ctx.beginPath(); ctx.ellipse(x, y - 57, w / 2 - 4, 5, 0, 0, 7); ctx.fillStyle = '#FFF3F8'; ctx.fill(); }
  // little sparkles
  ctx.strokeStyle = 'rgba(255,255,255,.9)'; ctx.lineWidth = 2.5; ctx.lineCap = 'round';
  for (let i = 0; i < 2; i++) { const a = t * 0.03 + i * 3, px = x + Math.cos(a) * (w / 2 + 16), py = y - 80 + Math.sin(a * 1.3) * 10; ctx.beginPath(); ctx.moveTo(px - 4, py); ctx.lineTo(px + 4, py); ctx.moveTo(px, py - 4); ctx.lineTo(px, py + 4); ctx.stroke(); }
  ctx.restore();
}
// The treasure chest (bell): the lid opens when the pearl touches it
function chest(ctx: Ctx2D, gl: GoalState, t: number, g: Gfx) {
  const hit = gl.hitT != null, k = hit ? clamp((t - gl.hitT!) / 20, 0, 1) : 0;
  ctx.save(); ctx.translate(gl.x, gl.y + 10);
  if (hit) { ctx.fillStyle = 'rgba(255,224,122,.5)'; ctx.beginPath(); ctx.arc(0, -10, 50 + Math.sin(t * 0.2) * 4, 0, 7); ctx.fill(); }
  g.shadowOn(ctx, 10, 5);
  rr(ctx, -36, -10, 72, 40, 8); ctx.fillStyle = g.lg(ctx, -10, 30, '#C98A4A', '#8A5328'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3);
  ctx.fillStyle = '#FFC93C'; rr(ctx, -36, -2, 72, 6, 2); ctx.fill(); rr(ctx, -6, -6, 12, 14, 3); ctx.fill(); outline(ctx, 2);
  // golden contents
  ctx.fillStyle = '#FFE07A'; ctx.beginPath(); ctx.ellipse(0, -12, 30, 8, 0, Math.PI, 0); ctx.fill();
  // lid
  ctx.save(); ctx.translate(0, -12); ctx.rotate(-k * 1.2);
  ctx.beginPath(); ctx.moveTo(-36, 0); ctx.lineTo(-36, -12); ctx.quadraticCurveTo(0, -34, 36, -12); ctx.lineTo(36, 0); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -30, 0, '#D99A5A', '#9A6030'); ctx.fill(); outline(ctx, 3);
  ctx.fillStyle = '#FFC93C'; ctx.fillRect(-36, -6, 72, 5);
  ctx.restore();
  ctx.restore();
}
// Bubble machine: small yellow machine with a nozzle and a propeller
function bubbleMachine(ctx: Ctx2D, g: Gfx, t: number, sel?: boolean) {
  g.shadowOn(ctx, 8, 4);
  rr(ctx, -34, -14, 68, 30, 10); ctx.fillStyle = g.lg(ctx, -14, 16, '#FFE07A', '#F2A516'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3);
  rr(ctx, -10, -28, 20, 16, 5); ctx.fillStyle = '#9FB3C8'; ctx.fill(); outline(ctx, 2.5);
  ctx.beginPath(); ctx.arc(-16, 2, 7, 0, 7); ctx.fillStyle = '#7FD3FF'; ctx.fill(); outline(ctx, 2);
  ctx.save(); ctx.translate(16, 2); ctx.rotate(t * 0.2);
  for (let i = 0; i < 3; i++) { ctx.rotate(Math.PI * 2 / 3); ctx.beginPath(); ctx.ellipse(0, -5, 3, 6, 0, 0, 7); ctx.fillStyle = '#FF5A5F'; ctx.fill(); }
  ctx.restore();
  if (sel) { rr(ctx, -40, -34, 80, 56, 14); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
}
// Conch that blows out a current (tip at the back, opening forward towards +x)
function conch(ctx: Ctx2D, g: Gfx, sel?: boolean) {
  g.shadowOn(ctx, 8, 4);
  ctx.beginPath(); ctx.moveTo(30, -20); ctx.quadraticCurveTo(10, -26, -18, -12); ctx.lineTo(-40, 0); ctx.lineTo(-18, 12); ctx.quadraticCurveTo(10, 26, 30, 20); ctx.quadraticCurveTo(40, 0, 30, -20); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -26, 26, '#FFE8C8', '#F2B27A'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3);
  ctx.strokeStyle = 'rgba(190,110,50,.55)'; ctx.lineWidth = 2.5;
  for (const x of [-22, -8, 6]) { ctx.beginPath(); ctx.moveTo(x, -16 + Math.abs(x) * 0.2); ctx.quadraticCurveTo(x + 6, 0, x, 16 - Math.abs(x) * 0.2); ctx.stroke(); }
  ctx.beginPath(); ctx.ellipse(32, 0, 8, 18, 0, 0, 7); ctx.fillStyle = '#FF8FB8'; ctx.fill(); outline(ctx, 2.5);
  if (sel) { rr(ctx, -48, -32, 92, 64, 16); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
}
// the current's stripes (move in the current's direction)
function stripes(ctx: Ctx2D, len: number, wid: number, t: number, v: number, alpha: number) {
  ctx.save();
  ctx.beginPath(); rr(ctx, 0, -wid / 2, len, wid, wid / 2); ctx.fillStyle = `rgba(150,220,255,${0.12 * alpha})`; ctx.fill();
  ctx.clip();
  ctx.strokeStyle = `rgba(255,255,255,${0.75 * alpha})`; ctx.lineWidth = 4; ctx.lineCap = 'round';
  const sp = 46, off = (t * v * 0.8) % sp;
  for (let row = -1; row <= 1; row++) {
    for (let x = -sp + off; x < len; x += sp) {
      const yy = row * wid * 0.28, xx = x + (row % 2 ? sp / 2 : 0);
      ctx.beginPath(); ctx.moveTo(xx, yy); ctx.lineTo(xx + 18, yy); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(xx + 12, yy - 6); ctx.lineTo(xx + 18, yy); ctx.lineTo(xx + 12, yy + 6); ctx.stroke();
    }
  }
  ctx.restore();
}
// The submarine float: yellow barrel with portholes – the air gauge shows how much air it holds
function floater(ctx: Ctx2D, w: number, h: number, air: number, g: Gfx, t?: number) {
  g.shadowOn(ctx, 8, 4);
  rr(ctx, -w / 2, -h / 2, w, h, h / 2); ctx.fillStyle = g.lg(ctx, -h / 2, h / 2, '#FFE07A', '#F2A516'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3);
  for (const x of [-w * 0.28, 0, w * 0.28]) { ctx.beginPath(); ctx.arc(x, 0, h * 0.24, 0, 7); ctx.fillStyle = air > 0.5 ? '#DDF6FF' : '#2B6CB0'; ctx.fill(); outline(ctx, 2); }
  // tower with air gauge
  rr(ctx, -12, -h / 2 - 16, 24, 18, 5); ctx.fillStyle = '#F2A516'; ctx.fill(); outline(ctx, 2.5);
  ctx.fillStyle = '#fff'; rr(ctx, -8, -h / 2 - 12, 16, 10, 3); ctx.fill();
  ctx.fillStyle = air > 0.5 ? '#37C16B' : '#FF5A5F'; rr(ctx, -8, -h / 2 - 12 + 10 * (1 - air), 16, 10 * air, 2); ctx.fill();
  if (air > 0.5 && t != null) for (let i = 0; i < 3; i++) { const s = (t * 1.2 + i * 14) % 40; bubble(ctx, -4 + i * 4, -h / 2 - 20 - s, 3 + i); }
}
function urchin(ctx: Ctx2D, r: number, t: number) {
  ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.lineCap = 'round';
  for (let i = 0; i < 14; i++) { const a = i / 14 * Math.PI * 2 + Math.sin(t * 0.05 + i) * 0.05; ctx.beginPath(); ctx.moveTo(Math.cos(a) * r * 0.6, Math.sin(a) * r * 0.6); ctx.lineTo(Math.cos(a) * r * 1.35, Math.sin(a) * r * 1.35); ctx.stroke(); }
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.fillStyle = '#B57BE8'; ctx.fill(); outline(ctx, 3);
  for (const s of [-1, 1]) { ctx.beginPath(); ctx.arc(s * r * 0.32, -r * 0.1, r * 0.14, 0, 7); ctx.fillStyle = INK; ctx.fill(); }
}
// Jellyfish (pad): soft bell with tentacles
function jellyfish(ctx: Ctx2D, w: number, h: number, sel: boolean, g: Gfx, t?: number) {
  const sq = Math.sin((t || 0) * 0.08) * 2;
  ctx.strokeStyle = '#C99AF0'; ctx.lineWidth = 4; ctx.lineCap = 'round';
  for (let i = -2; i <= 2; i++) { ctx.beginPath(); ctx.moveTo(i * w * 0.16, h / 2 - 2); ctx.quadraticCurveTo(i * w * 0.16 + 6, h / 2 + 12, i * w * 0.16 - 2, h / 2 + 22 + sq); ctx.stroke(); }
  ctx.beginPath(); ctx.moveTo(-w / 2, h / 2); ctx.quadraticCurveTo(-w / 2, -h / 2 - 4, 0, -h / 2 - 4); ctx.quadraticCurveTo(w / 2, -h / 2 - 4, w / 2, h / 2); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -h / 2, h / 2, '#F2DDFF', '#C99AF0'); ctx.globalAlpha = 0.95; ctx.fill(); ctx.globalAlpha = 1; outline(ctx, 3);
  for (const s of [-1, 1]) { ctx.beginPath(); ctx.arc(s * w * 0.12, 0, 2.6, 0, 7); ctx.fillStyle = INK; ctx.fill(); }
  ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(0, 2, 5, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke();
  if (sel) { rr(ctx, -w / 2 - 6, -h / 2 - 10, w + 12, h + 36, 14); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
}
// Kelp branch (plank)
function kelp(ctx: Ctx2D, w: number, h: number, sel: boolean, g: Gfx) {
  g.block(ctx, 0, 0, w, h, 0, '#7FE0A0', '#2FA35A', h / 2, { outline: sel ? '#FFC93C' : null });   // block only tests outline for truthiness
  ctx.fillStyle = '#5CC36B';
  for (let x = -w / 2 + 14; x < w / 2 - 8; x += 24) { ctx.beginPath(); ctx.ellipse(x, -h / 2 - 3, 7, 4, -0.4, 0, 7); ctx.fill(); outline(ctx, 1.5); }
}
// big transparent bubble (bubble balloon)
function bigBubble(ctx: Ctx2D, x: number, y: number, r: number, t: number) {
  const w = Math.sin((t || 0) * 0.1) * 1.5;
  ctx.beginPath(); ctx.ellipse(x, y, r + w, r - w, 0, 0, 7); ctx.fillStyle = 'rgba(220,245,255,.35)'; ctx.fill();
  ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(255,255,255,.95)'; ctx.stroke();
  ctx.lineWidth = 1.5; ctx.strokeStyle = 'rgba(59,42,92,.35)'; ctx.stroke();
  ctx.beginPath(); ctx.ellipse(x - r * .4, y - r * .4, r * .22, r * .12, -0.7, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.95)'; ctx.fill();
  ctx.beginPath(); ctx.arc(x + r * .35, y + r * .3, r * .3, 0.2, 1.4); ctx.strokeStyle = 'rgba(255,170,220,.6)'; ctx.lineWidth = 3; ctx.stroke();
}
// blue sea background behind the intro videos
function introSea(ctx: Ctx2D) {
  const g = ctx.createLinearGradient(0, 0, 0, 280); g.addColorStop(0, '#4FB0E8'); g.addColorStop(1, '#1F6FB0');
  ctx.fillStyle = g; ctx.fillRect(0, 0, 460, 280);
  ctx.fillStyle = '#F2D59B'; ctx.fillRect(0, 252, 460, 28);
}

// ---------- skins ----------
const skins: Record<string, SkinDef> = {
  ocean_stone(ctx, b, g) {
    const w = b.dw!, h = b.dh!;
    g.block(ctx, b.position.x, b.position.y, w, h, b.angle, '#9FB8D0', '#5E7A9A', Math.min(14, w / 2, h / 2));
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.fillStyle = 'rgba(255,255,255,.55)';
    for (let i = 0; i < Math.floor(w * h / 3000) + 1; i++) {
      const cx = -w / 2 + 12 + ((i * 47) % Math.max(1, w - 24)), cy = -h / 2 + 8 + ((i * 29) % Math.max(1, h - 16));
      if (Math.abs(cx) < w / 2 - 6 && Math.abs(cy) < h / 2 - 5) { ctx.beginPath(); ctx.arc(cx, cy, 3, 0, Math.PI); ctx.fill(); }
    }
    ctx.restore();
  },
  ocean_coral(ctx, b, g) {
    const w = b.dw!, h = b.dh!;
    g.block(ctx, b.position.x, b.position.y, w, h, b.angle, '#FFB3C8', '#E86A8E', Math.min(12, w / 2, h / 2));
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.fillStyle = 'rgba(255,255,255,.4)';
    for (let x = -w / 2 + 10; x < w / 2 - 6; x += 16) for (let y = -h / 2 + 8; y < h / 2 - 4; y += 14) { ctx.beginPath(); ctx.arc(x + (y % 28 ? 6 : 0), y, 2.2, 0, 7); ctx.fill(); }
    ctx.restore();
  },
  ocean_sand(ctx, b, g) {
    const w = b.dw!, h = b.dh!;
    g.block(ctx, b.position.x, b.position.y, w, h, b.angle, '#FBE3AE', '#E0B865', Math.min(12, w / 2, h / 2));
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.strokeStyle = 'rgba(190,140,60,.4)'; ctx.lineWidth = 2;
    for (let x = -w / 2 + 20; x < w / 2 - 20; x += 50) { ctx.beginPath(); ctx.moveTo(x, -h / 2 + 8); ctx.quadraticCurveTo(x + 10, -h / 2 + 4, x + 20, -h / 2 + 8); ctx.stroke(); }
    ctx.restore();
  },
};
const goal = {
  bowl(ctx: Ctx2D, gl: { x: number; y: number; w: number }, full: number, t: number, g: Gfx) { shellBowl(ctx, gl, full, t, g); },
  front(ctx: Ctx2D, gl: { x: number; y: number; w: number }) { const { x, y, w } = gl; ctx.save(); ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 + 6, 8, 0, 0, Math.PI); ctx.lineWidth = 5; ctx.strokeStyle = '#FFE3EE'; ctx.stroke(); ctx.restore(); },
  bell(ctx: Ctx2D, gl: GoalState, t: number, g: Gfx) { chest(ctx, gl, t, g); },
};
// light rays from the surface
function bgExtra(ctx: Ctx2D, W: number, H: number) {
  ctx.fillStyle = 'rgba(255,255,255,.08)';
  for (let i = 0; i < 5; i++) { const x = 100 + i * 250; ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x + 60, 0); ctx.lineTo(x + 160, H); ctx.lineTo(x + 40, H); ctx.closePath(); ctx.fill(); }
}

// ---------- parts ----------
const parts = {
  // Bubble machine: blows a column of air bubbles upwards – the bubbles lift things
  ocean_bubbles: definePart({
    rects: [{ x: 0, y: 0, w: 68, h: 30 }], rot: 15, minA: -45, maxA: 45, defaultA: 0,
    draw(ctx, pl, sel, g) {
      const a = (pl.a || 0) * DEG;
      ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a);
      ctx.fillStyle = 'rgba(255,255,255,.18)'; rr(ctx, -36, -480, 72, 450, 30); ctx.fill();
      ctx.setLineDash([6, 8]); ctx.strokeStyle = 'rgba(255,255,255,.6)'; ctx.lineWidth = 2.5; rr(ctx, -36, -480, 72, 450, 30); ctx.stroke(); ctx.setLineDash([]);
      [[0, -70, 6], [-10, -170, 9], [8, -270, 7], [-4, -370, 10], [6, -450, 8]].forEach(([x, y, r]) => bubble(ctx, x, y, r));
      bubbleMachine(ctx, g, 0, sel);
      ctx.restore();
    },
    icon(ctx, g) { ctx.save(); ctx.translate(0, 14); ctx.scale(0.85, 0.85); bubbleMachine(ctx, g, 0); ctx.restore(); bubble(ctx, 0, -22, 7); bubble(ctx, 8, -40, 5); },
    create(api, pl) {
      const a = (pl.a || 0) * DEG, dx = Math.sin(a), dy = -Math.cos(a);
      const b = api.add(api.R(pl.x, pl.y, 68, 30, { isStatic: true, angle: a, friction: 0.2, restitution: 0.1, chamfer: { radius: 10 }, label: 'ocean_bubbles' }));
      b.kind = 'ocean_bubbles';
      const C: Column = { x: pl.x + dx * 30, y: pl.y + dy * 30, dx, dy, len: 450, wid: 72, pow: 0.42 };
      const bub: Bub[] = []; let puff = -99;
      return {
        layer: 'mid',
        step(api) {
          const S = api.S;
          if (S.t % 5 === 0) bub.push({ s: 0, p: (((S.t * 37) % 17) - 8) * 1.8, r: 4 + (S.t * 13) % 6, ph: S.t * 0.7 });
          for (let i = bub.length - 1; i >= 0; i--) { const q = bub[i]; q.s += 3 + q.r * 0.15; if (q.s > C.len) bub.splice(i, 1); }
          column(api, C);
          for (const h of S.heroes) { const ddx = h.position.x - C.x, ddy = h.position.y - C.y, al = ddx * dx + ddy * dy, pe = ddx * -dy + ddy * dx; if (al > 0 && al < C.len && Math.abs(pe) < 40 && S.t - puff > 40) { puff = S.t; api.sfx('ocean_blub', h.position.x, h.position.y); } }
        },
        draw(ctx, t, g) {
          ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a);
          for (const q of bub) bubble(ctx, q.p + Math.sin(q.ph + q.s * 0.05) * 5, -30 - q.s, q.r);
          bubbleMachine(ctx, g, t);
          ctx.restore();
        },
      };
    },
    intro(ctx, t, g, h) {
      introSea(ctx);
      ctx.save(); ctx.translate(230, 240); bubbleMachine(ctx, g, t); ctx.restore();
      for (let i = 0; i < 8; i++) { const s = (t * 2.2 + i * 30) % 220; bubble(ctx, 230 + Math.sin(i * 2 + s * 0.05) * 6, 210 - s, 4 + (i % 3) * 2); }
      const k = (t % 240) / 240, by = 200 - h.ease(Math.min(1, k * 1.5)) * 150;
      g.ball(ctx, h.fakeBall(230, by, 0, -1, 16), 'hero', t);
    },
  }),
  // Conch: blows out a current in the direction it points
  ocean_conch: definePart({
    rects: [{ x: 0, y: 0, w: 80, h: 44 }], rot: 45, minA: -180, maxA: 180, defaultA: 0,
    draw(ctx, pl, sel, g) {
      const a = (pl.a || 0) * DEG;
      ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a);
      ctx.save(); ctx.translate(40, 0); stripes(ctx, 280, 96, performance.now() / 16, 3, 0.9); ctx.restore();
      conch(ctx, g, sel); ctx.restore();
    },
    icon(ctx, g) { ctx.save(); ctx.rotate(-0.3); ctx.scale(0.85, 0.85); conch(ctx, g); ctx.restore(); },
    create(api, pl) {
      const a = (pl.a || 0) * DEG, dx = Math.cos(a), dy = Math.sin(a);
      const b = api.add(api.R(pl.x, pl.y, 80, 44, { isStatic: true, angle: a, friction: 0.2, restitution: 0.1, chamfer: { radius: 14 }, label: 'ocean_conch' }));
      b.kind = 'ocean_conch';
      const Z: Zone = { x: pl.x + dx * 40, y: pl.y + dy * 40, dx, dy, len: 280, wid: 96, v: 3.2 };
      zones(api).push(Z);
      let vush = -99;
      return {
        layer: 'mid',
        step(api) {
          current(api, Z);
          for (const h of api.S.heroes) { const ddx = h.position.x - Z.x, ddy = h.position.y - Z.y, al = ddx * dx + ddy * dy, pe = ddx * -dy + ddy * dx; if (al > 0 && al < Z.len && Math.abs(pe) < Z.wid / 2 && api.S.t - vush > 50) { vush = api.S.t; api.sfx('ocean_whoosh', h.position.x, h.position.y); } }
        },
        draw(ctx, t, g) { ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a); ctx.save(); ctx.translate(40, 0); stripes(ctx, 280, 96, t, 3, 1); ctx.restore(); conch(ctx, g); ctx.restore(); },
      };
    },
    intro(ctx, t, g, h) {
      introSea(ctx);
      ctx.save(); ctx.translate(70, 140); ctx.save(); ctx.translate(40, 0); stripes(ctx, 340, 96, t, 3, 1); ctx.restore(); conch(ctx, g); ctx.restore();
      const k = (t % 240) / 240, bx = 180 + Math.max(0, k - 0.2) * 300, by = 40 + Math.min(k, 0.25) * 400;
      g.ball(ctx, h.fakeBall(Math.min(bx, 430), Math.min(by, 140), 2, 0, 16), 'hero', t);
    },
  }),
  // Jellyfish: soft bell that sends the pearl up in a strong hop
  ocean_jellyfish: definePart({
    rects: [{ x: 0, y: 0, w: 90, h: 30 }], rot: 15, minA: -45, maxA: 45, defaultA: 0,
    draw(ctx, pl, sel, g) { ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate((pl.a || 0) * DEG); jellyfish(ctx, 90, 30, sel, g, performance.now() / 16); ctx.restore(); },
    icon(ctx, g) { ctx.save(); ctx.translate(0, -8); jellyfish(ctx, 76, 28, false, g, 0); ctx.restore(); },
    create(api, pl) {
      const a = (pl.a || 0) * DEG, nx = Math.sin(a), ny = -Math.cos(a);
      const b = api.add(api.R(pl.x, pl.y, 90, 30, { isStatic: true, angle: a, friction: 0.1, restitution: 0.2, chamfer: { radius: 12 }, label: 'ocean_jellyfish' }));
      b.kind = 'ocean_jellyfish';
      const todo: GameBody[] = []; let hitT = -99;
      api.onHit(b, o => {
        if (!api.isDynamic(o)) return;
        if ((o.position.x - pl.x) * nx + (o.position.y - pl.y) * ny < 0) return;   // only from above
        todo.push(o);
      });
      return {
        layer: 'mid',
        after(api) {
          for (const o of todo.splice(0)) {
            const vt = o.velocity.x * -ny + o.velocity.y * nx;
            api.Body.setVelocity(o, { x: nx * 11 + -ny * vt * 0.5, y: ny * 11 + nx * vt * 0.5 });
            o.oceanBoost = 30; hitT = api.S.t;
            api.sfx('ocean_boing', o.position.x, o.position.y, '#E6CCFF', 6);
          }
        },
        draw(ctx, t, g, S) { const k = S.t - hitT, sq = k < 16 ? Math.sin(k / 16 * Math.PI) : 0; ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a); ctx.scale(1 + sq * 0.15, 1 - sq * 0.25); jellyfish(ctx, 90, 30, false, g, t); ctx.restore(); },
      };
    },
    intro(ctx, t, g, h) {
      introSea(ctx);
      const tt = t % 120, y = tt < 60 ? 60 + tt * 2.4 : 204 - Math.sin((tt - 60) / 60 * Math.PI) * 150;
      ctx.save(); ctx.translate(230, 236); jellyfish(ctx, 100, 30, false, g, t); ctx.restore();
      g.ball(ctx, h.fakeBall(230, Math.min(y, 204), 0, tt < 60 ? 2 : -2, 16), 'hero', t);
    },
  }),
  // Bubble balloon: the pearl that touches it is carried up – the balloon pops when it hits something
  ocean_balloon: definePart({
    rects: [{ x: 0, y: 0, w: 72, h: 72, circle: true }], circle: 36, rot: false,
    draw(ctx, pl, sel) { bigBubble(ctx, pl.x, pl.y, 36, 0); if (sel) { ctx.beginPath(); ctx.arc(pl.x, pl.y, 44, 0, 7); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); } },
    icon(ctx) { bigBubble(ctx, 0, 0, 30, 0); },
    create(api, pl) {
      const { Body, Bodies } = api;
      const b = api.add(Bodies.circle(pl.x, pl.y, 36, { isStatic: true, isSensor: true, label: 'ocean_balloon' }));
      b.kind = 'ocean_balloon';
      const st: { b: GameBody; x: number; y: number; held: GameBody | null; popped: boolean; popT: number; vy: number; vx: number } = { b, x: pl.x, y: pl.y, held: null, popped: false, popT: -99, vy: 0, vx: 0 };
      api.onHit(b, o => { if (!st.held && !st.popped && api.isBall(o)) { st.held = o; o.oceanHeld = true; api.sfx('ocean_blub', st.x, st.y, '#DDF6FF', 6); } });
      const statics = () => api.S.bodies.filter(x => x.isStatic && !x.isSensor && x !== b && x.kind !== 'ocean_balloon');
      function pop() {
        st.popped = true; st.popT = api.S.t; api.Body.setPosition(b, { x: -500, y: -500 });
        if (st.held) { st.held.oceanHeld = false; Body.setVelocity(st.held, { x: 0, y: 0.5 }); st.held = null; }
        api.sfx('ocean_pop', st.x, st.y, '#FFFFFF', 12);
      }
      return {
        layer: 'front',
        step(api) {
          if (st.popped || !st.held) return;
          st.vy = Math.max(-1.8, st.vy - 0.06);
          // currents move the balloon
          let fx = 0, fy = 0;
          for (const Z of zones(api)) if (inZone(Z, st.x, st.y)) { fx += Z.dx * Z.v; fy += Z.dy * Z.v; }
          st.vx += (fx - st.vx) * 0.06; st.vy += fy * 0.04;
          st.x += st.vx; st.y += st.vy; Body.setPosition(b, { x: st.x, y: st.y });
          const h = st.held;
          Body.setVelocity(h, { x: (st.x - h.position.x) * 0.3 + st.vx, y: (st.y + 8 - h.position.y) * 0.3 + st.vy });
          // pops at the ceiling, a stone, an urchin or the top of the level
          const probe = { x: st.x, y: st.y - 36 };
          let hit = st.y < 50;
          if (!hit) for (const s of statics()) if (api.Matter.Vertices.contains(s.vertices, probe) || api.Matter.Vertices.contains(s.vertices, { x: st.x - 22, y: st.y - 28 }) || api.Matter.Vertices.contains(s.vertices, { x: st.x + 22, y: st.y - 28 })) { hit = true; break; }
          if (!hit) for (const u of api.S.oceanUrchins || []) if (Math.hypot(u.x - st.x, u.y - st.y) < u.r + 44) hit = true;
          if (st.x < 30 || st.x > 1170) hit = true;
          if (hit) pop();
        },
        busy() { return !!st.held; },
        draw(ctx, t) {
          if (st.popped) { const k = (api.S.t - st.popT) / 20; if (k < 1) { ctx.strokeStyle = `rgba(255,255,255,${1 - k})`; ctx.lineWidth = 3; for (let i = 0; i < 8; i++) { const a = i * Math.PI / 4, r1 = 30 + k * 30; ctx.beginPath(); ctx.moveTo(st.x + Math.cos(a) * r1, st.y + Math.sin(a) * r1); ctx.lineTo(st.x + Math.cos(a) * (r1 + 10), st.y + Math.sin(a) * (r1 + 10)); ctx.stroke(); } } return; }
          bigBubble(ctx, st.x, st.y + Math.sin(t * 0.05) * (st.held ? 0 : 3), 36, t);
        },
      };
    },
    intro(ctx, t, g, h) {
      introSea(ctx);
      ctx.fillStyle = '#5E7A9A'; ctx.fillRect(250, 0, 210, 26);
      const tt = t % 240;
      let bx = 60 + Math.min(tt, 60) * 2.6, by = 220, sy = 220;
      if (tt > 60) { sy = Math.max(62, 220 - (tt - 60) * 2.2); by = sy + 8; bx = 216; }
      if (tt <= 150) bigBubble(ctx, 216, tt > 60 ? sy : 220, 34, t);
      else if (tt < 170) { ctx.strokeStyle = 'rgba(255,255,255,.8)'; ctx.lineWidth = 3; for (let i = 0; i < 8; i++) { const a = i * Math.PI / 4; ctx.beginPath(); ctx.moveTo(216 + Math.cos(a) * 34, 62 + Math.sin(a) * 34); ctx.lineTo(216 + Math.cos(a) * 46, 62 + Math.sin(a) * 46); ctx.stroke(); } }
      if (tt > 150) { bx = 216 + (tt - 150) * 0.5; by = Math.min(220, 70 + (tt - 150) * 1.6); }
      g.ball(ctx, h.fakeBall(bx, by, 0, 0, 16), 'hero', t);
    },
  }),
};

// ---------- objects ----------
interface CurrentSt { Z: Zone; a: number }
interface FloatSt { b: GameBody; w: number; h: number; air: number; goal: number }
interface PumpSt { a: number; on: boolean; C: Column; bub: Bub[] }
type UrchinSt = Record<string, never>;

const objs = {
  // Current in the level: { x, y (start), a (direction, degrees), len, wid, v }
  ocean_current: defineObj<CurrentSt>({
    layer: 'back',
    create(api, o: CurrentData) { const a = (o.a || 0) * DEG, Z = { x: o.x, y: o.y, dx: Math.cos(a), dy: Math.sin(a), len: o.len || 300, wid: o.wid || 100, v: o.v || 3 }; zones(api).push(Z); return { Z, a }; },
    step(api, st) { current(api, st.Z); },
    draw(ctx, st, t) { const o = st.o; ctx.save(); ctx.translate(o.x, o.y); ctx.rotate(st.a); stripes(ctx, st.Z.len, st.Z.wid, t, st.Z.v, 1); ctx.restore(); },
    intro(ctx, t, g, h) { parts.ocean_conch.intro(ctx, t, g, h); },
  }),
  // Submarine float: { x, y, w, air (0/1 at start), id (a button toggles the air), maxY, minY }
  ocean_float: defineObj<FloatSt>({
    layer: 'mid',
    create(api, o: FloatData) {
      const w = o.w || 150, h = 34;
      const b = api.add(api.R(o.x, o.y, w, h, { friction: 0.8, frictionStatic: 1, restitution: 0.05, density: 0.002, chamfer: { radius: 14 }, label: 'ocean_float' }));
      b.kind = 'ocean_float'; api.dynamic(b);
      const st = { b, w, h, air: o.air ? 1 : 0, goal: o.air ? 1 : 0 };
      b.oceanF = st.air ? 2.2 : 0.5;
      if (o.id) api.onAct(o.id, () => { st.goal = 1 - st.goal; api.sfx('ocean_air', b.position.x, b.position.y, '#DDF6FF', 10); });
      return st;
    },
    step(api, st: ObjSt<FloatSt, FloatData>) {
      const b = st.b, o = st.o;
      st.air += clamp(st.goal - st.air, -0.03, 0.03);
      b.oceanF = 0.5 + st.air * 1.7;
      // the float stays level and above its spot
      api.Body.setAngle(b, 0); api.Body.setAngularVelocity(b, 0);
      api.Body.setVelocity(b, { x: (o.x - b.position.x) * 0.1, y: clamp(b.velocity.y, -2.2, 2.2) });
    },
    // after physics: keep the float within its range (and completely still at the edge)
    after(api, st: ObjSt<FloatSt, FloatData>) {
      const b = st.b, o = st.o;
      if (o.minY != null && b.position.y <= o.minY) { api.Body.setPosition(b, { x: b.position.x, y: o.minY }); api.Body.setVelocity(b, { x: 0, y: 0 }); }
      if (o.maxY != null && b.position.y >= o.maxY) { api.Body.setPosition(b, { x: b.position.x, y: o.maxY }); api.Body.setVelocity(b, { x: 0, y: 0 }); }
    },
    busy(api, st) { return Math.abs(st.goal - st.air) > 0.01; },
    blockers(o: FloatData) { const w = o.w || 150; return [{ x: o.x - w / 3, y: o.y, r: 30 }, { x: o.x + w / 3, y: o.y, r: 30 }, { x: o.x, y: o.y, r: 30 }]; },
    draw(ctx, st, t, g) { const b = st.b; ctx.save(); ctx.translate(b.position.x, b.position.y); floater(ctx, st.w, st.h, st.air, g, t); ctx.restore(); },
    intro(ctx, t, g, h) {
      introSea(ctx);
      const tt = t % 240, air = tt < 80 ? 0 : Math.min(1, (tt - 80) / 30), y = tt < 80 ? 220 : Math.max(90, 220 - (tt - 80) * 1.3);
      ctx.save(); ctx.translate(230, y); floater(ctx, 140, 34, air, g, t); ctx.restore();
      g.ball(ctx, h.fakeBall(230, y - 34, 0, 0, 16), 'hero', t);
      if (tt > 70 && tt < 90) { ctx.fillStyle = '#FF5A5F'; ctx.beginPath(); ctx.arc(80, 240, 14, 0, 7); ctx.fill(); outline(ctx, 2.5); }
    },
  }),
  // Bubble pump in the level that only starts when a button is pressed: { x, y, a, id, len }
  ocean_pump: defineObj<PumpSt>({
    layer: 'mid',
    create(api, o: PumpData) {
      const a = (o.a || 0) * DEG, dx = Math.sin(a), dy = -Math.cos(a);
      const b = api.add(api.R(o.x, o.y, 68, 30, { isStatic: true, angle: a, friction: 0.2, restitution: 0.1, chamfer: { radius: 10 }, label: 'ocean_pump' }));
      b.kind = 'ocean_pump';
      const st: PumpSt = { a, on: !o.id, C: { x: o.x + dx * 30, y: o.y + dy * 30, dx, dy, len: o.len || 450, wid: 72, pow: 0.42 }, bub: [] };
      if (o.id) api.onAct(o.id, () => { if (!st.on) { st.on = true; api.sfx('ocean_air', o.x, o.y, '#DDF6FF', 10); } });
      return st;
    },
    step(api, st) {
      if (!st.on) return;
      const S = api.S, C = st.C;
      if (S.t % 5 === 0) st.bub.push({ s: 0, p: (((S.t * 37) % 17) - 8) * 1.8, r: 4 + (S.t * 13) % 6, ph: S.t * 0.7 });
      for (let i = st.bub.length - 1; i >= 0; i--) { const q = st.bub[i]; q.s += 3 + q.r * 0.15; if (q.s > C.len) st.bub.splice(i, 1); }
      column(api, C);
    },
    blockers(o) { return [{ x: o.x, y: o.y, r: 40 }]; },
    draw(ctx, st, t, g) {
      const o = st.o;
      ctx.save(); ctx.translate(o.x, o.y); ctx.rotate(st.a);
      if (!st.on) { ctx.setLineDash([6, 8]); ctx.strokeStyle = 'rgba(255,255,255,.45)'; ctx.lineWidth = 2.5; rr(ctx, -36, -30 - st.C.len, 72, st.C.len, 30); ctx.stroke(); ctx.setLineDash([]); }
      for (const q of st.bub) bubble(ctx, q.p + Math.sin(q.ph + q.s * 0.05) * 5, -30 - q.s, q.r);
      bubbleMachine(ctx, g, st.on ? t : 0);
      if (!st.on) { ctx.fillStyle = '#FF5A5F'; ctx.beginPath(); ctx.arc(-16, 2, 5, 0, 7); ctx.fill(); }
      ctx.restore();
    },
  }),
  // Sea urchin: its spines make the bubble balloon pop: { x, y, r }
  ocean_urchin: defineObj<UrchinSt>({
    layer: 'mid',
    create(api, o: UrchinData) {
      const b = api.add(api.Bodies.circle(o.x, o.y, o.r || 22, { isStatic: true, friction: 0.3, restitution: 0.2, label: 'ocean_urchin' }));
      b.kind = 'ocean_urchin';
      (api.S.oceanUrchins = api.S.oceanUrchins || []).push({ x: o.x, y: o.y, r: o.r || 22 });
      return {};
    },
    blockers(o: UrchinData) { return [{ x: o.x, y: o.y, r: (o.r || 22) + 14 }]; },
    draw(ctx, st: ObjSt<UrchinSt, UrchinData>, t) { ctx.save(); ctx.translate(st.o.x, st.o.y); urchin(ctx, st.o.r || 22, t); ctx.restore(); },
  }),
  // Only for the intro video of level 1: under water the pearl sinks slowly
  ocean_water: defineObj({
    intro(ctx, t, g, h) {
      introSea(ctx);
      const tt = t % 240;
      // left: in air (falls fast) – right: in water (sinks slowly)
      ctx.fillStyle = 'rgba(255,255,255,.85)'; ctx.fillRect(20, 20, 190, 230); ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.strokeRect(20, 20, 190, 230);
      const yA = Math.min(226, 40 + 0.03 * tt * tt), yW = Math.min(226, 40 + tt * 0.9);
      g.ball(ctx, h.fakeBall(115, yA, 0, 4, 16), 'hero', t);
      g.ball(ctx, h.fakeBall(330, yW, 0, 1, 16), 'hero', t);
      for (let i = 0; i < 3; i++) bubble(ctx, 330 + (i - 1) * 8, yW - 26 - i * 12, 3 + i);
    },
  }),
};
// The whole level is under water
function setup(api: PhysicsApi, level: Level) {
  api.thing({ layer: 'back', step(api) { water(api, level); } });
  for (const b of api.S.dyn) if (api.isBall(b)) b.oceanF = b.def && b.def.oceanF != null ? b.def.oceanF : null;
}

// ---------- sounds ----------
const sounds: Record<string, SoundFn> = {
  ocean_blub: (tone) => { tone(300, 0.12, 'sine', 0.16, 700); tone(450, 0.1, 'sine', 0.1, 900, 0.06); },
  ocean_pop: (tone, noise) => { tone(900, 0.06, 'sine', 0.18, 1600); noise(0.06, 0.12, 3000, 2); },
  ocean_whoosh: (tone, noise) => { noise(0.4, 0.2, 600, 0.6); },
  ocean_air: (tone, noise) => { noise(0.5, 0.18, 1200, 1); tone(220, 0.4, 'sine', 0.08, 440); },
  ocean_boing: (tone) => { tone(220, 0.25, 'sine', 0.18, 440); },
};

// ---------- reskins ----------
const reskin = {
  plank(ctx, pl, sel, g, rects) { const r = rects[0]; ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a); kelp(ctx, r.w, r.h, sel, g); ctx.restore(); },
  long_plank(ctx, pl, sel, g, rects) { const r = rects[0]; ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a); kelp(ctx, r.w, r.h, sel, g); ctx.restore(); },
  pad(ctx, pl, sel, g, rects) { const r = rects[0]; ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a); jellyfish(ctx, r.w, r.h + 10, sel, g, typeof performance !== 'undefined' ? performance.now() / 16 : 0); ctx.restore(); },
  stone(ctx: Ctx2D, pl: Placement, sel: boolean, g: Gfx) {
    // anchor stone: round stone with a small anchor
    const body = pl.body as GameBody | undefined;
    const a = body ? body.angle : 0;
    ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a);
    ctx.beginPath(); ctx.arc(0, 0, 24, 0, 7); ctx.fillStyle = g.lg(ctx, -24, 24, '#B8C6D6', '#6E8098'); ctx.fill(); outline(ctx, 3);
    ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(0, -12); ctx.lineTo(0, 10); ctx.moveTo(-10, 4); ctx.quadraticCurveTo(0, 16, 10, 4); ctx.moveTo(-6, -6); ctx.lineTo(6, -6); ctx.stroke();
    ctx.beginPath(); ctx.arc(0, -15, 3.5, 0, 7); ctx.stroke();
    if (sel) { ctx.beginPath(); ctx.arc(0, 0, 32, 0, 7); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
    ctx.restore();
  },
} satisfies WorldDef['reskin'];
const icons: WorldDef['icons'] = {
  plank(ctx, g) { ctx.save(); ctx.rotate(-0.35); kelp(ctx, 100, 16, false, g); ctx.restore(); },
  pad(ctx, g) { ctx.save(); ctx.translate(0, -8); jellyfish(ctx, 70, 26, false, g, 0); ctx.restore(); },
  stone(ctx, g) { reskin.stone(ctx, { t: 'stone', x: 0, y: 0 }, false, g); },
};

// ---------- meta ----------
const meta: WorldMeta = {
  icon: '🐙', strings, music: 'ocean.mp3',
  credits: { title: 'Jazz Cocktail Bar Funk', artist: 'alex-morgan', id: 559987, url: 'https://pixabay.com/music/funk-jazz-cocktail-bar-funk-559987/' },
  synth: { bpm: 88, root: 293.7, sc: 'dorian', prog: [0, 3, 0, 6], bar: 4, lead: 'pluck', dens: 0.4, sev: true },
};

export default defineWorld({
  k: K, meta, scene, wash: 'rgba(214,240,255,.5)', setup, bgExtra,
  skins, ball: drawBall, goal,
  parts, objs, sounds, reskin, icons,
  levels,
});
